<template lang="pug">
#other-user-safety-confirm-detail(v-cloak)
  .box
    .box-header.with-border.register-header
      .title
        span 施設関係者の安否確認情報（入力依頼内容、回答結果）
    .box-body.register-body
      FormLabel(key-name="受付状態" :value="_acceptingOtherUserReply")
      FormLabel(key-name="宛先" :value="receivers")
      FormLabel(key-name="メッセージ内容" :value="openingSentence")
      FormLabel(key-name="回答率" :value="responseRate")
        template(#textOption)
          .buttons.d-flex.flex-row
            //- 回答率（グループ）
            .loading.mr-2(v-if="readedOtherUsers == null && unreadOtherUsers == null")
              b-spinner.spinner
              span 回答率読込中...
            button.btn.respons-rate-detail-btn.mr-2(v-else @click="onResponseRateDetail()") 回答率（グループ）
            //- 回答者一覧
            .loading.mr-2(v-if="readedOtherUsers == null")
              b-spinner.spinner
              span 回答者読込中...
            button.btn.readed-families-btn.mr-2(v-else @click="onReadedOtherUsers(readedOtherUsers)") 回答者一覧（{{ readedOtherUsers.length }}人）
            //- 未回答者一覧
            .loading(v-if="unreadOtherUsers == null")
              b-spinner.spinner
              span 未回答者読込中...
            button.btn.unread-families-btn(v-else @click="onUnreadOtherUsers(unreadOtherUsers)") 未回答者一覧（{{ unreadOtherUsers.length }}人）
      .answers.register-row.form-label
        .register-group
          .register-key
              span.key-name 各質問の回答結果
          .register-value
            .d-flex.flex-column
              button.btn.answers-btn.mb-3(@click="gotoAnswers") 回答結果を表で確認する

              .d-flex.flex-column
                b-card.answer.mb-3(v-for="(confirm, index) in displayConfirms" no-body :key="`confirm-` + confirm.id")
                  b-card-header(header-tag="header" role="tab" header-class="accordion-toggle" v-b-toggle="'accordion-' + confirm.name")
                    span.toggle-text {{ confirm.label }}
                  b-collapse(:id="'accordion-' + confirm.name" :accordion="'accordion-' + confirm.name" role="tabpanel" @show="showAccordion(index)")
                    b-card-body
                      .d-flex.flex-column.align-items-end
                        .link-text(@click="onAnswerDetail(confirm, index)") 詳細情報

                      .answer-graph-parent.mb-3(:id="confirm.name + '-parent'")
                        .answer-graph(:id="confirm.name")
                      .table-options.d-flex.flex-row.justify-content-end.align-items-top
                        // フィルタリング
                        MultiSelect(
                          v-model="confirm.filter.groupIds" :options="confirm.filter.filterOptions"
                          key-name="施設関係者グループ" name="groupIds" :width="240"
                          :id="'filter-input-' + index" :name="'filter-input-' + index"
                          style="width: 240px; flex-grow: 0;"
                          @changeIds="filterGraphItem(index, $event)")

      .register-row(v-show="errorMessage")
        .register-group
          .register-value
            .error-message {{ errorMessage }}

    .box-footer.with-border.register-footer
      .buttons
        button.btn.change-status-btn(@click="showConfirmChangeStatusModal" type="button") 受付状態の変更
        button.btn.delete-btn(@click="showConfirmDeleteModal" type="button") 削除
        button.btn.cancel-btn(@click="gotoSafetyConfirmList" type="button") 戻る

  OkCancelModal(v-model="showChangeStatusModal" :title="changeStatusModalTitle" @ok="doChangeStatus")
  OkModal(v-model="showChangeStatusSuccessModal" title="受付状態の変更に成功しました。" @ok="createView")

  OkCancelModal(v-model="showDeleteModal" title="施設関係者の安否確認情報を削除しますか？" @ok="doDelete")
  OkModal(v-model="showDeleteSuccessModal" title="削除に成功しました。" @ok="gotoSafetyConfirmList")

  SafetyConfirmOtherUsersResponseRateModal(v-model="showResponseDateDetailModal" :items="responseRateDetailItems")
  SafetyConfirmOtherUsersModal(v-model="showSafetyConfirmOtherUsersModal" :items="showTargetOtherUsers" :filterOptions="otherUserGroupFilterOptions")

  OtherUserSafetyConfirmAnswerDetailsModal(v-model="showAnswerDetailModal" :title="answerDetailModalTitle" :items="answerDetailModalItems" 
    :confirm="answerDetailModalConfirm")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";
import { ChartData } from "chart.js";
import { PcUrl } from "@/commons/constants/PcUrl";
import SystemError from "@/commons/models/SystemError";

import OtherUserGroupsService from "@/commons/services/OtherUserGroups";
import OtherUserGroupModel from "@/commons/models/OtherUserGroup";
import OtherUserSafetyConfirmMastersService, {
  OtherUserSafetyConfirmMasterInputType,
  OtherUserSafetyConfirmMasterRadioInputOption,
  OtherUserSafetyConfirmMastersGetModel
} from "@/commons/services/OtherUserSafetyConfirmMasters";
import SendSafetyConfirmMessagesService, { SendOtherUserSafetyConfirmLineMessagesGetModel } from "@/commons/services/SendSafetyConfirmMessages";
import SafetyConfirmsService, { SafetyConfirmsGetModel, OtherUserSafetyConfirmsPatchModel } from "@/commons/services/SafetyConfirms";
import SafetyConfirmReplyMessagesService, { SafetyConfirmReplyMessagesGetModel } from "@/commons/services/SafetyConfirmReplyMessages";
import OtherUserGroupTypesService from "@/commons/services/OtherUserGroupTypes";
import OtherUserGroupTypeModel from "@/commons/models/OtherUserGroupType";
import OtherUserGroupSendMessageReadMembersService, { OtherUserGroupSendMessageReadMemberModel } from "@/commons/services/OtherUserGroupSendMessageReadMembers";

import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import DoughnutChart from "@/pc/basics/SafetyConfirmDoughnutChart.vue";
import FormLabel from "@/pc/components/forms/FormLabel.vue";
import SafetyConfirmOtherUsersModal, { SafetyConfirmOtherUserModelTableItem } from "@/pc/components/modals/SafetyConfirmOtherUsersModal.vue";
import SafetyConfirmOtherUsersResponseRateModal, { SafetyConfirmOtherUsersResponseRateModalItem } from "@/pc/components/modals/SafetyConfirmOtherUsersResponseRateModal.vue";
import MultiSelect from "@/pc/containers/safetyConfirms/MultiSelect.vue";
import OtherUserSafetyConfirmAnswerDetailsModal from "@/pc/containers/safetyConfirms/OtherUserSafetyConfirmAnswerDetailsModal.vue";
import {
  OtherUserSafetyConfirmAnswerDetailsModalTableField,
  OtherUserSafetyConfirmAnswerDetailsModalTableItem
} from "@/pc/containers/safetyConfirms/OtherUserSafetyConfirmAnswerDetailsTable.vue";

/**
 * 使用機能の確認画面コンポーネント
 */
@Component({
  components: {
    OkCancelModal,
    OkModal,
    PageLoading,
    DoughnutChart,
    FormLabel,
    SafetyConfirmOtherUsersModal,
    SafetyConfirmOtherUsersResponseRateModal,
    MultiSelect,
    OtherUserSafetyConfirmAnswerDetailsModal
  }
})
export default class OtherUserSafetyConfirmDetail extends Vue {
  /** 施設関係者グループサービス */
  @Inject("OtherUserGroupsService") otherUserGroupsService!: OtherUserGroupsService;
  /** 安否確認(施設関係者)で送信する質問内容のマスタサービス */
  @Inject("OtherUserSafetyConfirmMastersService") otherUserSafetyConfirmMastersService!: OtherUserSafetyConfirmMastersService;
  /** 安否確認(施設関係者)メッセージ送信サービス */
  @Inject("SendSafetyConfirmMessagesService") sendSafetyConfirmMessagesService!: SendSafetyConfirmMessagesService;
  /** 安否確認サービス */
  @Inject("SafetyConfirmsService") safetyConfirmsService!: SafetyConfirmsService;
  /** 安否確認回答サービス */
  @Inject("SafetyConfirmReplyMessagesService") safetyConfirmReplyMessagesService!: SafetyConfirmReplyMessagesService;
  /** 施設関係者グループ種類サービス */
  @Inject("OtherUserGroupTypesService") otherUserGroupTypesService!: OtherUserGroupTypesService;
  /** 送信メッセージにひもづくメンバーサービス */
  @Inject("OtherUserGroupSendMessageReadMembersService") otherUserGroupSendMessageReadMembersService!: OtherUserGroupSendMessageReadMembersService;
  // グラフの色は仮
  readonly graphColors: string[] = [
    "rgba(131, 205, 208, 1)",
    "rgba(230, 220, 105, 1)",
    "rgba(147, 186, 250, 1)",
    "rgba(161, 206, 151, 1)",
    "rgba(249, 204, 216, 1)",
    "rgba(253, 188, 103, 1)",
    "rgba(199, 182, 216, 1)",
    "rgba(219, 173, 137, 1)",
    "rgba(185, 185, 185, 1)"
  ];
  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";

  /** 回答率詳細モーダル表示可否 */
  showResponseDateDetailModal: boolean = false;
  /** 回答率詳細モーダル表示情報 */
  responseRateDetailItems: Array<SafetyConfirmOtherUsersResponseRateModalItem> = [];

  /** 回答者施設関係者モーダル表示可否 */
  showSafetyConfirmOtherUsersModal: boolean = false;
  /** 回答者施設関係者モーダル表示対象の施設関係者 */
  showTargetOtherUsers: Array<OtherUserGroupSendMessageReadMemberModel> = [];
  /** 回答者施設関係者モーダルフィルタリング */
  otherUserGroupFilterOptions: Array<{ value: string; text: string }> = [];

  /** 状態変更確認モーダルタイトル */
  changeStatusModalTitle: string = "";
  /** 状態変更確認モーダル表示可否 */
  showChangeStatusModal: boolean = false;
  /** 状態変更完了モーダル表示可否 */
  showChangeStatusSuccessModal: boolean = false;

  /** 削除確認モーダル表示可否 */
  showDeleteModal: boolean = false;
  /** 削除完了モーダル表示可否 */
  showDeleteSuccessModal: boolean = false;

  /** 施設関係者グループ */
  otherUserGroups: Array<OtherUserGroupModel> = [];
  /** 施設関係者グループ種類MAP */
  otherUserGroupTypeMap: Map<number, string> = new Map<number, string>();

  /** 安否確認受付状態 */
  acceptingOtherUserReply: boolean = false;
  /** 安否確認宛先 */
  receivers: string = "";
  /** 安否確認冒頭文 */
  openingSentence: string = "";
  /** 安否確認回答率 */
  responseRate: string = "";

  /** 回答者 */
  readedOtherUsers: Array<SafetyConfirmOtherUserModelTableItem> | null = null;
  /** 未回答者 */
  unreadOtherUsers: Array<SafetyConfirmOtherUserModelTableItem> | null = null;

  /** 安否確認グラフの最大文字数 */
  maxLabelLength: number = 0;
  /** 安否確認の回答リスト */
  displayConfirms: Array<Confirm> = [];

  /** 安否確認 */
  safetyConfirm: SafetyConfirmsGetModel | null = null;
  /** 安否確認(施設関係者)メッセージ */
  messages: Array<SendOtherUserSafetyConfirmLineMessagesGetModel> = [];
  /** 安否確認(施設関係者)の回答 */
  replyMessages: Array<SafetyConfirmReplyMessagesGetModel> = [];
  /** 施設関係者への安否確認質問のMAP */
  mastersMap: Map<number, OtherUserSafetyConfirmMastersGetModel> = new Map<number, OtherUserSafetyConfirmMastersGetModel>();

  /** 回答詳細モーダル表示可否 */
  showAnswerDetailModal: boolean = false;
  /** 回答詳細モーダルタイトル */
  answerDetailModalTitle: string = "";
  /** 回答詳細モーダル列 */
  answerDetailModalFields: Array<OtherUserSafetyConfirmAnswerDetailsModalTableField> = [];
  /** 回答詳細モーダル行 */
  answerDetailModalItems: Array<OtherUserSafetyConfirmAnswerDetailsModalTableItem> = [];
  /** モーダル表示対象の安否確認 */
  answerDetailModalConfirm: Confirm | null = null;

  /** グラフインスタンス */
  graphInstances: Array<DoughnutChart> = [];

  /** エラーメッセージ */
  errorMessage: string = "";

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    await this.createView();
    return;
  }

  /**
   * ### vue lifecycle beforeDestroy
   */
  public beforeDestroy(): void {
    // 既存DoughnutChartインスタンスを削除する
    for (const instance of this.graphInstances) {
      if (instance) {
        instance.$destroy();
      }
    }
  }

  /**
   * ### 画面情報作成
   *
   * - 表示対象の質問情報のMAPを作る
   */
  public async createView(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    const otherUserGroups: Array<OtherUserGroupModel> = await this.getOtherUserGroups();
    const otherUserGroupFilterOptions: Array<{ value: string; text: string }> = await this.createOtherUserGroupFilterOptions(otherUserGroups);
    this.otherUserGroups = otherUserGroups;
    this.otherUserGroupFilterOptions = otherUserGroupFilterOptions;

    const masters: Array<OtherUserSafetyConfirmMastersGetModel> = await this.getOtherUserSafetyConfirmMasters();
    const mastersMap: Map<number, OtherUserSafetyConfirmMastersGetModel> = this.createMasterMap(masters);
    this.mastersMap = mastersMap;

    const safetyConfirmsId: number = Number(this.$route.params.id);
    const safetyConfirm: SafetyConfirmsGetModel = await this.getSafetyConfirm(safetyConfirmsId);
    this.safetyConfirm = safetyConfirm;
    this.acceptingOtherUserReply = safetyConfirm.acceptingOtherUserReply;

    console.debug("safetyConfirm:", safetyConfirm);

    let messages: Array<SendOtherUserSafetyConfirmLineMessagesGetModel> = [];
    for (const sortKey of safetyConfirm.otherUserMessagesSortKey as string[]) {
      const message: SendOtherUserSafetyConfirmLineMessagesGetModel = await this.getMessage(sortKey);
      console.debug(`sortKey:${sortKey}, message:`, message);
      messages = messages.concat(message);
    }
    this.messages = messages;

    let replyMessages: Array<SafetyConfirmReplyMessagesGetModel> = [];
    for (const sortKey of safetyConfirm.otherUserMessagesSortKey as string[]) {
      replyMessages = replyMessages.concat(await this.getSafetyConfirmReplyMessages(sortKey));
    }
    this.replyMessages = replyMessages;

    const receivers: string[] = [];
    for (const message of messages) {
      for (const receiver of message.receiverDetails) {
        receivers.push(receiver.groupName);
      }
    }
    this.receivers = [...new Set(receivers)].join("、");
    this.openingSentence = messages[0].functionContents.openingSentence;

    const latestReplyMessagesCount: number = this.latestReplyMessagesCount(replyMessages);
    let memberCount: number = 0;
    for (const message of messages) {
      for (const receiverDetail of message.receiverDetails) {
        memberCount += receiverDetail.members.length;
      }
    }
    const percentage: number = ((latestReplyMessagesCount / memberCount) * 10000) / 100;
    this.responseRate = `${latestReplyMessagesCount}／${memberCount}人（${percentage.toFixed(2)}％）`;

    const maxLabelLength: number = this.checkMaxLabelLength(messages[0].functionContents.confirmationContents, mastersMap);
    this.maxLabelLength = maxLabelLength;

    const displayConfirms: Array<Confirm> = this.createDisplayConfirms(messages, mastersMap, replyMessages, maxLabelLength);
    this.displayConfirms = displayConfirms;

    const otherUserGroupTypes: Array<OtherUserGroupTypeModel> = await this.getOtherGroupTypes();
    const otherUserGroupTypeMap: Map<number, string> = this.createOtherUserGroupTypesMap(otherUserGroupTypes);
    this.otherUserGroupTypeMap = otherUserGroupTypeMap;

    // 非同期で回答者、未回答者、回答率詳細を取得する
    if (safetyConfirm.otherUserMessagesSortKey != null) {
      this.setOtherUsers(safetyConfirm.otherUserMessagesSortKey, otherUserGroupTypeMap);
    }
    // DoughnutChartはmounted前に子コンポーネントが存在していない場合は描画されない
    // しかし、質問数は不明のためDoughnutChartを何個表示するかが分からないため、情報取得してドキュメントの描画が終わったら動的にDoughnutChartを追加する
    this.$nextTick(() => {
      // 既存DoughnutChartインスタンスを削除する
      for (const instance of this.graphInstances) {
        instance.$destroy();
      }
      // インスタンスを削除したのでリストを初期化する
      this.graphInstances = [];

      for (const displayConfirm of displayConfirms) {
        const graphInstance: any = this.addGraph(displayConfirm);
        this.graphInstances.push(graphInstance);
      }
    });
    this.isBusy = false;
    return;
  }

  /**
   * ### 施設関係者グループを取得する
   */
  public async getOtherUserGroups(): Promise<Array<OtherUserGroupModel>> {
    let response: Array<OtherUserGroupModel> = [];

    try {
      response = await this.otherUserGroupsService.gets(userStore);
    } catch (error) {
      const systemerror: SystemError = error as SystemError;
      // 413エラーの場合、全件取得した結果がペイロード制限に引っかかったことになるので、index指定で段階的に取得する
      if (systemerror.statusCode == 413) {
        const count: number = await this.otherUserGroupsService.getCount(userStore);
        const loopEnd: number = Math.ceil(count / 300);

        for (let i = 0; i < loopEnd; i++) {
          const params: any = {
            startIndex: i * 300,
            endIndex: i * 300 + 300
          };
          response = response.concat(await this.otherUserGroupsService.gets(userStore, params));
        }
      }
    }
    return response;
  }

  /**
   * ### 施設関係者グループフィルター用選択項目
   *
   * - 検索用に施設関係者グループマスターのMAPを作る
   * - アカウント情報から許可されている施設関係者グループIDの配列を取得する
   * - 施設関係者グループマスターと照合して選択項目の施設関係者グループを作成する
   */
  public createOtherUserGroupFilterOptions(otherUserGroups: Array<OtherUserGroupModel>): Array<{ value: string; text: string }> {
    const filterOptions: Array<{ value: string; text: string }> = [];
    filterOptions.push({
      value: "",
      text: "全て"
    });

    const groupMap: Map<number, OtherUserGroupModel> = new Map<number, OtherUserGroupModel>();
    for (const group of otherUserGroups) {
      groupMap.set(group.id, group);
    }

    const allowedGroups: number[] = userStore.loginUser.permissions.otherUserGroups;
    for (const allowedGroup of allowedGroups) {
      const group: OtherUserGroupModel | undefined = groupMap.get(allowedGroup);
      if (group) {
        filterOptions.push({
          value: `${group.id}`,
          text: group.name
        });
      }
    }

    return filterOptions;
  }

  /**
   * ### 最新の返信メッセージ数を取得する
   */
  public latestReplyMessagesCount(replyMessages: Array<SafetyConfirmReplyMessagesGetModel>): number {
    let latestReplyMessagesCount: number = 0;
    for (const replyMessage of replyMessages) {
      if (replyMessage.latest == true) {
        latestReplyMessagesCount++;
      }
    }
    return latestReplyMessagesCount;
  }

  /**
   * ### 安否確認(施設関係者)の回答を取得する
   */
  public async getSafetyConfirmReplyMessages(messagesSortKey: string): Promise<Array<SafetyConfirmReplyMessagesGetModel>> {
    const response: Array<SafetyConfirmReplyMessagesGetModel> = await this.safetyConfirmReplyMessagesService.gets(userStore, messagesSortKey);
    return response;
  }

  /**
   * ### 安否確認(施設関係者)で送信する質問内容のマスタを取得する
   */
  public async getOtherUserSafetyConfirmMasters(): Promise<Array<OtherUserSafetyConfirmMastersGetModel>> {
    const response: Array<OtherUserSafetyConfirmMastersGetModel> = await this.otherUserSafetyConfirmMastersService.get(userStore);
    return response;
  }

  /**
   * ### 質問内容マスタのMAPを作成する
   */
  public createMasterMap(masters: Array<OtherUserSafetyConfirmMastersGetModel>): Map<number, OtherUserSafetyConfirmMastersGetModel> {
    let map: Map<number, OtherUserSafetyConfirmMastersGetModel> = new Map<number, OtherUserSafetyConfirmMastersGetModel>();
    for (const master of masters) {
      map.set(master.id, master);
    }
    return map;
  }

  /**
   * ### 安否確認を取得する
   */
  public async getSafetyConfirm(safetyConfirmsId: number): Promise<SafetyConfirmsGetModel> {
    const response: SafetyConfirmsGetModel = await this.safetyConfirmsService.get(userStore, safetyConfirmsId);
    return response;
  }

  /**
   * ### 安否確認(施設関係者)メッセージを取得する
   */
  public async getMessage(parentOtherUserMessagesSortKey: string): Promise<SendOtherUserSafetyConfirmLineMessagesGetModel> {
    const response: SendOtherUserSafetyConfirmLineMessagesGetModel = await this.sendSafetyConfirmMessagesService.getOtherUserMessage(userStore, parentOtherUserMessagesSortKey);
    return response;
  }

  /**
   * ### 画面表示用の質問項目を作成する
   *
   * - 安否確認メッセージで質問したものだけを返す
   * - 回答内容をグラフ表示するための情報を作成する
   *   - グラフの都合上ラベルの文字数を統一する必要があるので統一する
   *   - グラフ表示するか否かは質問の入力種別で判断する(現在はラジオボタンのみ)
   *   - グラフでは各施設関係者からの回答を集計した情報を表示する
   *   - フィルタリングがあるので、回答を集計した情報は表示用とソースの両方を保持する
   *   - フィルタリング用プロパティを初期化する
   */
  public createDisplayConfirms(
    messages: Array<SendOtherUserSafetyConfirmLineMessagesGetModel>,
    mastersMap: Map<number, OtherUserSafetyConfirmMastersGetModel>,
    replyMessages: Array<SafetyConfirmReplyMessagesGetModel>,
    maxLabelLength: number
  ): Array<Confirm> {
    let displayConfirms: Array<Confirm> = [];

    for (const confirm of messages[0].functionContents.confirmationContents) {
      let displayConfirm: Confirm | undefined = mastersMap.get(confirm);
      if (displayConfirm != undefined) {
        switch (displayConfirm.inputType) {
          case OtherUserSafetyConfirmMasterInputType.RADIO: {
            const answerCountMap: Map<string, number> = this.createAnswerCountMap(replyMessages, displayConfirm.id);
            const chartjsData: ChartData = this.createRadioDoughnutChartData(displayConfirm, maxLabelLength, answerCountMap);
            displayConfirm.rateGraph = chartjsData;
            break;
          }
          case OtherUserSafetyConfirmMasterInputType.MULTIPLE_TEXT: {
            const chartjsData: ChartData = this.createMultipleTextDoughnutChartData(displayConfirm, maxLabelLength, replyMessages, displayConfirm.id);
            displayConfirm.rateGraph = chartjsData;
            break;
          }
          default: {
            break;
          }
        }

        displayConfirm.displayRateGraph = Object.assign({}, displayConfirm.rateGraph);

        const groupIds: string[] = [];
        for (const option of this.otherUserGroupFilterOptions) {
          groupIds.push(option.value);
        }

        displayConfirm.filter = {
          groupIds: groupIds,
          filterOptions: this.createOtherUserGroupGraphFilterOptions(this.otherUserGroups)
        };

        displayConfirms.push(displayConfirm);
      }
    }

    return displayConfirms;
  }

  /**
   * ### 施設関係者グループフィルター用選択項目
   *
   * - 検索用に施設関係者グループマスターのMAPを作る
   * - アカウント情報から許可されている施設関係者グループIDの配列を取得する
   * - 施設関係者グループマスターと照合して選択項目の施設関係者グループを作成する
   */
  public createOtherUserGroupGraphFilterOptions(otherUserGroups: Array<OtherUserGroupModel>): Array<{ value: string; text: string }> {
    const filterOptions: Array<{ value: string; text: string }> = [];
    const groupMap: Map<number, OtherUserGroupModel> = new Map<number, OtherUserGroupModel>();
    for (const group of otherUserGroups) {
      groupMap.set(group.id, group);
    }

    const allowedGroups: number[] = userStore.loginUser.permissions.otherUserGroups;
    for (const allowedGroup of allowedGroups) {
      const group: OtherUserGroupModel | undefined = groupMap.get(allowedGroup);
      if (group) {
        filterOptions.push({
          value: `${group.id}`,
          text: group.name
        });
      }
    }

    return filterOptions;
  }

  /**
   * ### 画面で使用する質問事項の中で最大文字数のラベルの文字数を調べる
   */
  public checkMaxLabelLength(confirms: number[], mastersMap: Map<number, OtherUserSafetyConfirmMastersGetModel>): number {
    let maxLabelLength: number = 0;
    for (const confirm of confirms) {
      let displayConfirm: Confirm | undefined = mastersMap.get(confirm);
      if (displayConfirm != undefined) {
        switch (displayConfirm.inputType) {
          case OtherUserSafetyConfirmMasterInputType.RADIO: {
            const inputOptions: OtherUserSafetyConfirmMasterRadioInputOption = displayConfirm.inputOptions as OtherUserSafetyConfirmMasterRadioInputOption;
            for (const choice of inputOptions.choices) {
              if (maxLabelLength < choice.label.length) {
                maxLabelLength = choice.label.length;
              }
            }
          }
        }
      }
    }
    return maxLabelLength;
  }

  /**
   * ### 質問IDに対して、質問の回答を集計したMAPを作成する
   *
   * - 最新の情報でなければ集計対象ではない
   */
  public createAnswerCountMap(replyMessages: Array<SafetyConfirmReplyMessagesGetModel>, answerId: number): Map<string, number> {
    let answerCountMap: Map<string, number> = new Map<string, number>();
    for (const replyMessage of replyMessages) {
      if (replyMessage.latest == false) {
        continue;
      }
      for (const answer of replyMessage.answers) {
        if (answer.id == answerId) {
          let answerCount: number | undefined = answerCountMap.get(answer.answer);
          if (answerCount != undefined) {
            answerCount++;
            answerCountMap.set(answer.answer, answerCount);
          } else {
            answerCountMap.set(answer.answer, 1);
          }
        }
      }
    }
    return answerCountMap;
  }

  /**
   * ### DoughnutChart用のグラフ情報を作成する
   */
  public createRadioDoughnutChartData(displayConfirm: Confirm, maxLabelLength: number, answerCountMap: Map<string, number>): ChartData {
    let counts: number[] = [];
    let labels: string[] = [];
    let backgroundColors: string[] = [];
    let borderWidths: Array<number> = [];
    const inputOptions: OtherUserSafetyConfirmMasterRadioInputOption = displayConfirm.inputOptions as OtherUserSafetyConfirmMasterRadioInputOption;

    for (const choice of inputOptions.choices) {
      labels.push(this.paddingGraphLabel(choice.label, maxLabelLength));
      let count: number | undefined = answerCountMap.get(choice.value);
      if (count != undefined) {
        counts.push(count);
      } else {
        counts.push(0);
      }
      backgroundColors.push(this.graphColors[Number(choice.value)]);
      borderWidths.push(0);
    }
    const chartjsData: ChartData = {
      labels: labels,
      datasets: [
        {
          label: displayConfirm.label,
          backgroundColor: backgroundColors,
          borderWidth: borderWidths,
          data: counts
        }
      ]
    };
    return chartjsData;
  }

  /**
   * ### DoughnutChart用のグラフ情報を作成する
   */
  public createMultipleTextDoughnutChartData(
    displayConfirm: Confirm,
    maxLabelLength: number,
    replyMessages: Array<SafetyConfirmReplyMessagesGetModel>,
    answerId: number
  ): ChartData {
    let inputedCount: number = 0;
    let noninputedCount: number = 0;
    for (const replyMessage of replyMessages) {
      if (replyMessage.latest == false) {
        continue;
      }
      for (const answer of replyMessage.answers) {
        if (answer.id == answerId) {
          if (answer.answer) {
            inputedCount++;
          } else {
            noninputedCount++;
          }
        }
      }
    }
    const chartjsData: ChartData = {
      labels: [this.paddingGraphLabel("入力", maxLabelLength), this.paddingGraphLabel("未入力", maxLabelLength)],
      datasets: [
        {
          label: displayConfirm.label,
          backgroundColor: [this.graphColors[0], this.graphColors[1]],
          borderWidth: [0, 0],
          data: [inputedCount, noninputedCount]
        }
      ]
    };
    return chartjsData;
  }

  /**
   * ### 文字数を引数の最大文字数に統一する
   *
   * - 置き換え文字は「・」
   */
  public paddingGraphLabel(label: string, maxLenght: number): string {
    let paddingText: string = "";
    for (let i = label.length; i < maxLenght; i++) {
      paddingText += "・";
    }
    return `${label}${paddingText}`;
  }

  /**
   * ### 施設関係者グループ種類を取得する
   */
  public async getOtherGroupTypes(): Promise<Array<OtherUserGroupTypeModel>> {
    const otherUserGroupTypes: Array<OtherUserGroupTypeModel> = await this.otherUserGroupTypesService.get(userStore);
    return otherUserGroupTypes;
  }

  /**
   * ### 画面表示用に施設関係者グループ種類のMAPを作成する
   */
  public createOtherUserGroupTypesMap(otherUserGroupTypes: Array<OtherUserGroupTypeModel>): Map<number, string> {
    let map: Map<number, string> = new Map<number, string>();
    for (const type of otherUserGroupTypes) {
      map.set(type.id, type.name);
    }
    return map;
  }

  /**
   * ### 確認、未確認の施設関係者と回答率詳細を設定する
   */
  public async setOtherUsers(otherUserMessagesSortKey: string[], otherUserGroupTypeMap: Map<number, string>): Promise<void> {
    let readedOtherUsers: Array<SafetyConfirmOtherUserModelTableItem> = [];
    let unreadOtherUsers: Array<SafetyConfirmOtherUserModelTableItem> = [];

    let groupMembersToIdMap: Map<number, Array<SafetyConfirmOtherUserModelTableItem>> = new Map<number, Array<SafetyConfirmOtherUserModelTableItem>>();

    for (const sortKey of otherUserMessagesSortKey) {
      const splitedSortKey: string[] = sortKey.split("_");
      const datetime: number = Number(splitedSortKey[2]);

      const otherUsers: Array<OtherUserGroupSendMessageReadMemberModel> = await this.getSendMessageReadOtherUsers(datetime);

      for (const otherUser of otherUsers) {
        console.debug(`setOtherUsers() otherUser:`, otherUser);

        const typeName: string | undefined = otherUserGroupTypeMap.get(otherUser.groupTypeId);
        const otherUserAddTypeName: SafetyConfirmOtherUserModelTableItem = {
          ...otherUser,
          groupTypeName: typeName ? typeName : ""
        };

        // 施設関係者メンバーを既読・未読配列に格納する
        if (otherUserAddTypeName.readDatetime != undefined || otherUserAddTypeName.readDatetime != null) {
          readedOtherUsers.push(otherUserAddTypeName);
        } else {
          unreadOtherUsers.push(otherUserAddTypeName);
        }

        // 施設関係者グループIDをkey、メンバー配列をvalueにしたMAPに格納する
        let groupMembers: Array<SafetyConfirmOtherUserModelTableItem> | undefined = groupMembersToIdMap.get(otherUserAddTypeName.groupId);
        if (groupMembers != undefined) {
          groupMembers.push(otherUserAddTypeName);
          groupMembersToIdMap.set(otherUserAddTypeName.groupId, groupMembers);
        } else {
          groupMembersToIdMap.set(otherUserAddTypeName.groupId, [otherUserAddTypeName]);
        }
      }
    }

    this.readedOtherUsers = readedOtherUsers;
    this.unreadOtherUsers = unreadOtherUsers;

    let items: Array<SafetyConfirmOtherUsersResponseRateModalItem> = [];
    groupMembersToIdMap.forEach((members: Array<SafetyConfirmOtherUserModelTableItem>, id: number) => {
      let responseCount: number = 0;
      let nonResponseCount: number = 0;

      for (const member of members) {
        if (member.readDatetime != undefined || member.readDatetime != null) {
          responseCount++;
        } else {
          nonResponseCount++;
        }
      }

      const percentage: number = ((responseCount / members.length) * 10000) / 100;
      const responseRate: string = percentage.toFixed(2);

      items.push({
        id,
        groupTypeName: members[0].groupTypeName,
        groupName: members[0].groupName,
        targetCount: members.length,
        responseCount,
        nonResponseCount,
        responseRate
      });
    });
    this.responseRateDetailItems = items;

    return;
  }

  /**
   * ### メッセージ送信したメッセージにひもづく施設関係者を取得する
   */
  public async getSendMessageReadOtherUsers(datetime: number): Promise<Array<OtherUserGroupSendMessageReadMemberModel>> {
    const otherUsers: Array<OtherUserGroupSendMessageReadMemberModel> = await this.otherUserGroupSendMessageReadMembersService.get(userStore, datetime);
    return otherUsers;
  }

  /**
   * ### グラフを画面に追加する
   *
   * - DoughnutChartは画面作成時にコンポーネントが存在しないと描画が正しく行われない
   * - 画面のDOM要素にDoughnutChartのVueインスタンスを動的に作成して追加する
   * - ただPropを指定しても描画されないのでchartDataChanged()を呼び出して無理やり描画させている
   * - あとで削除できるようにインスタンスは保持しておく
   * - 実行時に既に登録されているインスタンスは削除する
   *   - あわせて作られているinnerHtmlも削除する
   */
  public addGraph(displayConfirm: Confirm): any {
    // DoughnutChartを追加するDOMを取得
    const element: HTMLElement | null = document.getElementById(displayConfirm.name);
    if (element != null) {
      // 既に子要素がある場合があるので削除する
      element.innerHTML = "";
      // DoughnutChartのインスタンスを作成
      const doughnutChart = Vue.extend(DoughnutChart);
      const instance: any = new doughnutChart({
        propsData: {
          chartData: displayConfirm.displayRateGraph,
          height: 240,
          width: 640
        }
      });
      /** 今後のため、グラフのクリックイベントのフックの仕方をコメントで残しておく
       * イベントは src\pc\basics\SafetyConfirmDoughnutChart.vue からemitされる
      instance.$on("onAnswer", function(index: number) {
        alert(`clicked index:${index}`);
      });
      */
      // インスタンスをマウント
      instance.$mount(element);
      // 無理やり描画を実施
      instance.chartDataChanged();

      return instance;
    }

    return null;
  }

  /**
   * ### 回答率の詳細をモーダル表示する
   */
  public onResponseRateDetail(): void {
    this.showResponseDateDetailModal = true;
    return;
  }

  /**
   * ### 回答者施設関係者をモーダル表示する
   */
  public onReadedOtherUsers(readedOtherUsers: Array<OtherUserGroupSendMessageReadMemberModel>): void {
    this.showTargetOtherUsers = readedOtherUsers;
    this.showSafetyConfirmOtherUsersModal = true;
    return;
  }

  /**
   * ### 未回答者施設関係者をモーダル表示する
   */
  public onUnreadOtherUsers(unreadOtherUsers: Array<OtherUserGroupSendMessageReadMemberModel>): void {
    this.showTargetOtherUsers = unreadOtherUsers;
    this.showSafetyConfirmOtherUsersModal = true;
    return;
  }

  /**
   * ### 回答者詳細画面へ遷移する
   */
  public gotoAnswers(): void {
    const safetyConfirmsId: number = Number(this.$route.params.id);
    this.$router.push({ name: PcUrl.OtherUserSafetyConfirmAnswers, params: { id: `${safetyConfirmsId}` } });
    return;
  }

  /**
   * ### 状態変更確認モーダルを表示する
   */
  public showConfirmChangeStatusModal(): void {
    const safetyConfirm: SafetyConfirmsGetModel = this.safetyConfirm as SafetyConfirmsGetModel;
    if (safetyConfirm.acceptingOtherUserReply == true) {
      this.changeStatusModalTitle = "受付状態を受付停止に変更しますか？";
    } else {
      this.changeStatusModalTitle = "受付状態を受付中に変更しますか？";
    }
    this.showChangeStatusModal = true;
    return;
  }

  /**
   * ### 安否確認の受付状態を更新する
   */
  public async doChangeStatus(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "更新中...";
    try {
      const safetyConfirm: SafetyConfirmsGetModel = this.safetyConfirm as SafetyConfirmsGetModel;
      const requestBody: OtherUserSafetyConfirmsPatchModel = this.createChangeStatusRequestBody(safetyConfirm);
      await this.safetyConfirmsService.patchOtherUser(userStore, safetyConfirm.id, requestBody);
    } catch (error) {
      // ステータスコードが409の場合、他端末にて更新が行われているので、画面情報を再取得してから再入力を促す
      const systemError: SystemError = error as SystemError;
      if (systemError.statusCode == 409) {
        await this.createView();
        this.errorMessage = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
      }
      // その他のステータスコードの場合は上位に例外を投げなおす
      else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }
    this.showChangeStatusSuccessModal = true;
    return;
  }

  /**
   * ### 安否確認の受付状態更新のリクエストボディを作成する
   */
  public createChangeStatusRequestBody(safetyConfirm: SafetyConfirmsGetModel): OtherUserSafetyConfirmsPatchModel {
    const requestBody: OtherUserSafetyConfirmsPatchModel = {
      otherUserMessagesSortKey: safetyConfirm.otherUserMessagesSortKey ? safetyConfirm.otherUserMessagesSortKey : null,
      sendedOtherUserLine: safetyConfirm.sendedOtherUserLine,
      sendedOtherUserMail: safetyConfirm.sendedOtherUserMail,
      acceptingOtherUserReply: !safetyConfirm.acceptingOtherUserReply,
      updated: safetyConfirm.updated
    };
    return requestBody;
  }

  /**
   * ### 削除確認モーダルを表示する
   */
  public showConfirmDeleteModal(): void {
    this.showDeleteModal = true;
    return;
  }

  /**
   * ### 安否確認の施設関係者情報を削除する
   */
  public async doDelete(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "削除中...";
    try {
      const safetyConfirm: SafetyConfirmsGetModel = this.safetyConfirm as SafetyConfirmsGetModel;
      const requestBody: OtherUserSafetyConfirmsPatchModel = this.createDeleteRequestBody(safetyConfirm);
      await this.safetyConfirmsService.patchOtherUser(userStore, safetyConfirm.id, requestBody);
      this.showDeleteSuccessModal = true;
    } catch (error) {
      // ステータスコードが409の場合、他端末にて更新が行われているので、画面情報を再取得してから再入力を促す
      const systemError: SystemError = error as SystemError;
      if (systemError.statusCode == 409) {
        await this.createView();
        this.errorMessage = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
      }
      // その他のステータスコードの場合は上位に例外を投げなおす
      else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }
    return;
  }

  /**
   * ### 安否確認の施設関係者情報の削除のリクエストボディを作成する
   */
  public createDeleteRequestBody(safetyConfirm: SafetyConfirmsGetModel): OtherUserSafetyConfirmsPatchModel {
    const requestBody: OtherUserSafetyConfirmsPatchModel = {
      otherUserMessagesSortKey: null,
      sendedOtherUserLine: false,
      sendedOtherUserMail: false,
      acceptingOtherUserReply: false,
      updated: safetyConfirm.updated
    };
    return requestBody;
  }

  /**
   * ### アコーディオンのオープンイベント
   *
   * - アコーディオンがクローズするとDoughnutChartのheightが0になってしまい、オープン⇒クローズ⇒オープンでグラフが表示されなくなる
   * - アコーディオンがオープンされたタイミングで再描画を実施する
   */
  public showAccordion(index: number): void {
    const graphInstance: DoughnutChart = this.graphInstances[index];
    // graphInstanceがnullの場合がありえるようなのでチェックしてから実施する
    if (graphInstance) {
      graphInstance.chartDataChanged();
    }
    return;
  }

  /**
   * ### グラフの内容をフィルタリングしたもので再描画する
   */
  public filterGraphItem(index: number): void {
    const filter: ConfirmFilter = this.displayConfirms[index].filter as ConfirmFilter;
    const groupIds: string[] = filter.groupIds as string[];
    const idMap: Map<number, string> = new Map();
    for (const groupId of groupIds) {
      idMap.set(Number(groupId), groupId);
    }

    const filterdReplyMessages: Array<any> = this.replyMessages.concat().filter((message) => {
      return idMap.has(message.groupId);
    });

    switch (this.displayConfirms[index].inputType) {
      case OtherUserSafetyConfirmMasterInputType.RADIO: {
        const answerCountMap: Map<string, number> = this.createAnswerCountMap(filterdReplyMessages, this.displayConfirms[index].id);
        const chartjsData: ChartData = this.createRadioDoughnutChartData(this.displayConfirms[index], this.maxLabelLength, answerCountMap);
        this.displayConfirms[index].displayRateGraph = chartjsData;
        break;
      }
      case OtherUserSafetyConfirmMasterInputType.MULTIPLE_TEXT: {
        const chartjsData: ChartData = this.createMultipleTextDoughnutChartData(
          this.displayConfirms[index],
          this.maxLabelLength,
          filterdReplyMessages,
          this.displayConfirms[index].id
        );
        this.displayConfirms[index].displayRateGraph = chartjsData;
        break;
      }
      default: {
        break;
      }
    }

    // DoughnutChartはmounted前に子コンポーネントが存在していない場合は描画されない
    // しかし、質問数は不明のためDoughnutChartを何個表示するかが分からないため、情報取得してドキュメントの描画が終わったら動的にDoughnutChartを追加する
    this.$nextTick(() => {
      // DoughnutChartを追加する親要素を取得する
      const parentElement: HTMLElement = document.getElementById(`${this.displayConfirms[index].name}-parent`) as HTMLElement;

      // 既存DoughnutChartインスタンスを削除する
      // インスタンスを削除してもdivのゴミが残るので削除する
      this.graphInstances[index].$destroy();
      parentElement.removeChild(parentElement.firstElementChild as HTMLElement);

      // 新しいDoughnutChartインスタンスを格納するdiv要素を作成し、idを付与して親要素に追加する
      const childElement = document.createElement("div");
      childElement.setAttribute("id", this.displayConfirms[index].name);
      parentElement.appendChild(childElement);

      // グラフインスタンスを作成しdiv要素に追加する
      const instance: any = this.addGraph(this.displayConfirms[index]);
      // あとで削除できるようにインスタンスは保持しておく
      this.graphInstances[index] = instance;
    });

    return;
  }

  /**
   * ### 回答の詳細モーダルを表示する
   */
  onAnswerDetail(confirm: Confirm, index: number): void {
    this.answerDetailModalTitle = confirm.label;
    this.answerDetailModalConfirm = confirm;
    this.answerDetailModalItems = this.createAnswerDetailModalItems(confirm, index);
    this.showAnswerDetailModal = true;
    return;
  }

  private createAnswerDetailModalItems(confirm: Confirm, index: number): Array<OtherUserSafetyConfirmAnswerDetailsModalTableItem> {
    const items: Array<OtherUserSafetyConfirmAnswerDetailsModalTableItem> = [];

    for (const message of this.replyMessages) {
      let answer: string = "";
      switch (confirm.inputType) {
        case OtherUserSafetyConfirmMasterInputType.RADIO: {
          const option: OtherUserSafetyConfirmMasterRadioInputOption = confirm.inputOptions as OtherUserSafetyConfirmMasterRadioInputOption;
          for (const choice of option.choices) {
            if (choice.value == message.answers[index].answer) {
              answer = choice.label;
              break;
            }
          }
          break;
        }
        case OtherUserSafetyConfirmMasterInputType.MULTIPLE_TEXT: {
          answer = message.answers[index].answer ? message.answers[index].answer : "";
          break;
        }
        default: {
          break;
        }
      }

      const groupName: string | undefined = this.otherUserGroupTypeMap.get(message.groupId);
      items.push({
        answer: answer,
        answerDatetime: message.updated,

        groupId: message.groupId,
        groupTypeName: groupName ? groupName : "",
        groupName: message.groupName,
        memberName: message.groupMemberName,
        memberTel: message.groupMemberTel,
        memberOrganization: message.groupMemberOrganization
      });
    }

    return items;
  }

  /**
   * ### 安否確認一覧画面に遷移する
   */
  public gotoSafetyConfirmList(): void {
    this.$router.push(PcUrl.SafetyConfirmList);
    return;
  }

  get _acceptingOtherUserReply(): string {
    if (this.acceptingOtherUserReply == true) {
      return "受付中";
    } else {
      return "受付停止";
    }
  }
}

export interface Confirm extends OtherUserSafetyConfirmMastersGetModel {
  /** 回答グラフ作成で使用するグラフデータ */
  rateGraph?: ChartData;
  /** 表示するグラフデータ */
  displayRateGraph?: ChartData;
  /** フィルター用情報 */
  filter?: ConfirmFilter;
}

export interface ConfirmFilter {
  /** フィルタリング対象 */
  groupIds?: string[];
  /** フィルタリングオプション */
  filterOptions?: Array<{ value: string; text: string }>;
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";
// 親要素からネストしてスタイルを指定する
#other-user-safety-confirm-detail {
  max-width: 820px;
  margin-left: auto;
  margin-right: auto;
  .opening-sentence {
    white-space: pre-line;
  }
  .answers {
    .answer {
      width: 100%;
    }
    .answer:last-child {
      margin-bottom: 0;
    }
    .accordion-toggle {
      width: 680px;
    }
  }
  .answers-btn {
    width: 240px;
  }
  .delete-btn,
  .cancel-btn {
    width: 130px;
  }
}
</style>
